import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { buildProductUrl } from "../../utils/navigation";
import {
  Search,
  Heart,
  ShoppingCart,
  ChevronDown,
  ChevronRight,
  Menu,
  Loader2,
  LogOut,
  X,
} from "lucide-react";
import { useCategoriesWithProducts } from "../../hooks/useSupabase";
import { useCartStore } from "../../store/cartStore";
import { getProducts } from "../../services/products.service";
import { navigateToCategory } from "../../utils/navigation";
import { useAuth } from "../../hooks/useAuth";
import { getPrimaryProductImageUrl } from "../../utils/imageUtils";
import { useWishlistContext } from "../../hooks/useWishlistContext";
import { logSearchActivity } from "../../services/analytics.service";

const MobileHeader = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expandedCat, setExpandedCat] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTotalCount, setSearchTotalCount] = useState(0);

  const { categoriesWithSubs } = useCategoriesWithProducts();
  const displayCategories = categoriesWithSubs || [];

  const { wishlistedIds } = useWishlistContext();
  const wishlistCount = wishlistedIds.length;

  const cartCount = useCartStore((s) =>
    s.items.reduce((sum, item) => sum + item.quantity, 0)
  );

  // Close drawer on resize to desktop
  useEffect(() => {
    const onResize = () => { if (window.innerWidth >= 768) setDrawerOpen(false); };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Search
  useEffect(() => {
    if (searchQuery.length <= 2) {
      setShowSearchResults(false);
      setSearchResults([]);
      setSearchTotalCount(0);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    const t = setTimeout(() => {
      const q = searchQuery.trim();
      if (!q) { setSearchLoading(false); return; }
      getProducts({ search: q, limit: 20, offset: 0 })
        .then(({ products, totalCount }) => { setSearchResults(products || []); setSearchTotalCount(totalCount); setShowSearchResults(true); })
        .catch(() => { setSearchResults([]); setSearchTotalCount(0); setShowSearchResults(false); })
        .finally(() => setSearchLoading(false));
    }, 300);
    return () => { clearTimeout(t); setSearchLoading(false); };
  }, [searchQuery]);

  const handleSearchEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && searchQuery.length > 2) {
      setShowSearchResults(false);
      logSearchActivity({ query: searchQuery, eventType: "search", resultCount: searchTotalCount, userId: user?.id });
      navigate(`/products?search=${encodeURIComponent(searchQuery)}&sort=popular`, {
        state: { searchQuery, sort: "popular" },
      });
      setSearchQuery("");
      (e.target as HTMLInputElement).blur();
    }
  };

  const handleSearchButtonClick = () => {
    if (searchQuery.length > 2) {
      setShowSearchResults(false);
      logSearchActivity({ query: searchQuery, eventType: "search", resultCount: searchTotalCount, userId: user?.id });
      navigate(`/products?search=${encodeURIComponent(searchQuery)}&sort=popular`, {
        state: { searchQuery, sort: "popular" },
      });
      setSearchQuery("");
    }
  };

  const handleWishlistClick = () => {
    if (!user) { navigate("/registration"); return; }
    navigate("/wishlist");
  };
  const handleCartClick = () => {
    if (!user) { navigate("/registration"); return; }
    navigate("/cart");
  };

  return (
    <div className="w-full bg-white md:hidden">

      {/* ── Row 1: menu | logo | icons ── */}
      <div className="px-[18px] py-3 border-b border-header-border flex items-center justify-between">

        <button
          onClick={() => setDrawerOpen(true)}
          className="flex items-center justify-center w-9 h-9 text-primary"
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5" strokeWidth={1.8} />
        </button>

        <Link to="/" className="text-center">
          <div
            className="text-[21px] font-normal text-primary leading-none"
            style={{ fontFamily: "var(--font-display)" }}
          >
            My <span className="italic text-accent">Perfume</span>
          </div>
          <div className="flex items-center justify-center gap-[5px] mt-1">
            <div className="h-px w-[14px] bg-accent" />
            <span className="text-[7.5px] text-header-muted tracking-[2px] uppercase">
              Signature Scent
            </span>
            <div className="h-px w-[14px] bg-accent" />
          </div>
        </Link>

        <div className="flex items-center gap-[14px] text-primary">
          <button onClick={handleWishlistClick} className="relative" aria-label="Wishlist">
            <Heart className="w-5 h-5" strokeWidth={1.5} />
            {wishlistCount > 0 && (
              <span className="absolute -top-1 -right-1 w-[15px] h-[15px] rounded-full bg-accent text-white flex items-center justify-center text-[8px] font-bold">
                {wishlistCount}
              </span>
            )}
          </button>
          <button onClick={handleCartClick} className="relative" aria-label="Cart">
            <ShoppingCart className="w-5 h-5" strokeWidth={1.5} />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 w-[15px] h-[15px] rounded-full bg-accent text-white flex items-center justify-center text-[8px] font-bold">
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* ── Row 2: search ── */}
      <div className="px-[18px] py-[10px] border-b border-header-border relative">
        <div className="flex border border-header-border overflow-hidden" style={{ borderRadius: 2 }}>
          <input
            type="text"
            placeholder="Search products, brands..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => searchQuery && setShowSearchResults(true)}
            onKeyDown={handleSearchEnter}
            className="flex-1 px-[13px] py-[10px] text-[13px] text-primary placeholder:text-header-muted bg-header-surface focus:outline-none border-none"
          />
          <button className="w-[42px] bg-accent flex items-center justify-center shrink-0" onClick={handleSearchButtonClick}>
            {searchLoading
              ? <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
              : <Search className="w-3.5 h-3.5 text-primary" />}
          </button>
        </div>

        {/* Search results */}
        {showSearchResults && searchResults.length > 0 && (
          <div className="absolute top-full left-0 right-0 bg-white border border-header-border shadow-xl z-50 max-h-72 overflow-y-auto">
            {searchResults.slice(0, 8).map((product) => (
              <div
                key={product.id}
                className="flex items-center px-4 py-3 hover:bg-header-surface cursor-pointer border-b border-header-border last:border-b-0"
                onClick={() => {
                  logSearchActivity({ query: searchQuery, eventType: "click", resultCount: searchTotalCount, clickedProductId: product.id, userId: user?.id });
                  setShowSearchResults(false);
                  setSearchQuery("");
                  navigate(buildProductUrl(product.id, product.name, product.brand_name));
                }}
              >
                <img
                  src={getPrimaryProductImageUrl(product.image_paths || []) || ""}
                  alt={product.name}
                  className="w-10 h-10 object-cover mr-3 shrink-0"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-primary truncate">{product.name}</div>
                  <div className="text-[11px] text-header-muted truncate">{product.description}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Row 3: category pills ── */}
      {/* <div
        className="px-[18px] py-[9px] flex gap-2 overflow-x-auto border-b border-header-border"
        style={{ scrollbarWidth: "none" }}
      >
        <Link
          to="/products"
          className="px-[15px] py-[7px] shrink-0 text-[12px] text-white bg-primary border border-primary cursor-pointer whitespace-nowrap"
          style={{ borderRadius: 2 }}
        >
          All Perfumes
        </Link>
        {displayCategories.map((cat) => (
          <Link
            key={cat.id}
            to={navigateToCategory(cat.id)}
            className="px-[15px] py-[7px] shrink-0 text-[12px] text-primary bg-transparent border border-header-border cursor-pointer whitespace-nowrap hover:border-primary transition-colors"
            style={{ borderRadius: 2 }}
          >
            {cat.name}
          </Link>
        ))}
      </div> */}

      {/* ── Drawer backdrop ── */}
      {drawerOpen && (
        <div
          className="fixed inset-0 bg-black/35 z-40"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      {/* ── Drawer ── */}
      <div
        className={`fixed top-0 left-0 h-full z-50 flex flex-col bg-white shadow-2xl transition-transform duration-300 ${drawerOpen ? "translate-x-0" : "-translate-x-full"}`}
        style={{ width: 290 }}
      >
        {/* Drawer header */}
        <div className="px-5 py-4 border-b border-header-border flex items-center justify-between shrink-0">
          <div>
            <div
              className="text-[20px] font-normal text-primary leading-none"
              style={{ fontFamily: "var(--font-display)" }}
            >
              My <span className="italic text-accent">Perfume</span>
            </div>
            <div className="text-[9px] text-header-muted tracking-[2px] uppercase mt-[3px]">
              Signature Scent
            </div>
          </div>
          <button
            onClick={() => setDrawerOpen(false)}
            className="text-header-muted hover:text-primary transition-colors"
            aria-label="Close menu"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Drawer nav */}
        <div className="flex-1 overflow-y-auto">

          {/* All Perfumes */}
          <Link
            to="/products"
            onClick={() => setDrawerOpen(false)}
            className="px-5 py-[15px] flex items-center justify-between border-b border-header-border text-[13.5px] font-medium text-primary hover:bg-header-surface transition-colors"
          >
            <span>All Perfumes</span>
            <ChevronRight className="w-[13px] h-[13px] text-header-muted" />
          </Link>

          {/* Categories accordion */}
          {displayCategories.map((cat) => (
            <div key={cat.id}>
              <button
                onClick={() => setExpandedCat(expandedCat === cat.id.toString() ? null : cat.id.toString())}
                className="w-full px-5 py-[15px] flex items-center justify-between border-b border-header-border text-[13.5px] text-primary cursor-pointer transition-colors"
                style={{
                  borderLeft: expandedCat === cat.id.toString() ? "2px solid var(--color-accent)" : "2px solid transparent",
                  background: expandedCat === cat.id.toString() ? "rgba(201,169,110,0.04)" : "transparent",
                }}
              >
                <span>{cat.name}</span>
                <ChevronDown
                  className={`w-3 h-3 text-header-muted transition-transform duration-200 ${expandedCat === cat.id.toString() ? "rotate-180" : ""}`}
                />
              </button>

              {expandedCat === cat.id.toString() && cat.sub_categories?.length > 0 && (
                <div className="bg-header-surface border-b border-header-border">
                  {cat.sub_categories.map((sub: any, i: number) => (
                    <Link
                      key={sub.id}
                      to={navigateToCategory(sub.id)}
                      onClick={() => setDrawerOpen(false)}
                      className="flex items-center justify-between px-5 py-[11px] pl-8 text-[13px] text-primary hover:text-accent transition-colors"
                      style={{
                        borderBottom: i < cat.sub_categories.length - 1 ? "1px solid var(--color-header-border)" : "none",
                      }}
                    >
                      <span>{sub.name}</span>
                      <ChevronRight className="w-[13px] h-[13px] text-header-muted" />
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Extra links */}
          {[
            { label: "New Arrivals", to: "/products?sort=newest" },
            { label: "Most Popular", to: "/products?sort=popular" },
            // { label: "Gift Sets", to: "/products?gift=true" },
          ].map((link) => (
            <Link
              key={link.label}
              to={link.to}
              onClick={() => setDrawerOpen(false)}
              className="px-5 py-[15px] flex items-center justify-between border-b border-header-border text-[13.5px] text-primary hover:bg-header-surface transition-colors"
            >
              <span>{link.label}</span>
              <ChevronRight className="w-[13px] h-[13px] text-header-muted" />
            </Link>
          ))}

          {/* User */}
          {user ? (
            <div className="px-5 py-4 border-b border-header-border">
              <div className="text-[11px] text-header-muted mb-2">Signed in as</div>
              <div className="text-[13px] font-medium text-primary truncate mb-1">
                {user.user_metadata?.full_name || user.user_metadata?.first_name || user.email?.split("@")[0]}
              </div>
              <div className="text-[11px] text-header-muted truncate mb-3">{user.email}</div>
              <button
                onClick={() => { signOut(); setDrawerOpen(false); }}
                className="flex items-center gap-2 text-[13px] text-primary hover:text-accent transition-colors"
              >
                <LogOut className="w-3.5 h-3.5" />
                <span>Sign Out</span>
              </button>
            </div>
          ) : (
            <Link
              to="/registration"
              onClick={() => setDrawerOpen(false)}
              className="mx-5 my-4 flex items-center justify-center py-3 bg-primary text-white text-[12px] font-medium tracking-[1.5px] uppercase hover:bg-primary-light transition-colors"
            >
              Sign In / Register
            </Link>
          )}
        </div>

        {/* Drawer footer: contact + social */}
        <div className="px-5 py-4 border-t border-header-border bg-header-surface shrink-0">
          <div className="text-[10.5px] text-header-muted mb-[6px]">Contact us</div>
          <div className="text-[12px] text-primary mb-[3px]">+961 81 389 098</div>
          <div className="text-[12px] text-primary">sales@myperfume.group</div>
          <div className="flex gap-2 mt-3">
            <a
              href="https://www.facebook.com/myperfume.group"
              target="_blank"
              rel="noopener noreferrer"
              className="w-7 h-7 rounded-full bg-[#1877f2] flex items-center justify-center hover:opacity-90 transition-opacity"
              aria-label="Facebook"
            >
              <span className="text-white text-[11px] font-bold leading-none">f</span>
            </a>
            <a
              href="https://www.instagram.com/myperfume.group"
              target="_blank"
              rel="noopener noreferrer"
              className="w-7 h-7 rounded-full flex items-center justify-center hover:opacity-90 transition-opacity"
              style={{ background: "linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)" }}
              aria-label="Instagram"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round">
                <rect x="2" y="2" width="20" height="20" rx="5" />
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileHeader;
